import type { NuxtApp } from '#app/nuxt';
import { useRuntimeConfig } from '#imports';
import { loadScript } from '~/utils/dom/loadScript';
import { useInitialOnce } from '../../composables/useInitialOnce';
import { onConsentStudioScan } from '../consent-studio';
import { matomoState } from './state';

export async function initMatomo(nuxt: NuxtApp): Promise<boolean> {
    console.log('[Matomo]: Init');

    const defaults = {
        enabled: true,
        debug: false,
        cloudId: undefined,
        containerId: undefined,
        host: undefined,
        siteId: undefined,
        enableLinkTracking: true,
        disableCookies: true,
        enableHeartBeatTimer: false,
        heartBeatTimerInterval: 15,
        trackerFileName: "matomo.js",
        crossOrigin: undefined,
        scriptInterval: 50,
        scriptTimeout: 5000,
    };

    const runtime = useRuntimeConfig();

    const apiConfig: any = useInitialOnce<any>('matomo') ?? await useApi('/setting/matomo');
    const options = { ...defaults, ...runtime.public.matomo, ...apiConfig };

    if (!options.enabled) {
        if (options.debug) {
            console.debug("[Matomo] Plugin has been disabled");
        }

        return false;
    }

    if (options.debug) {
        console.debug(`[Matomo] Plugin has been ${options.enabled ? 'enabled' : 'disabled'}.`);
    }

    if (!options.enabled) return false;

    let host = options.cloudId
        ? `https://${options.cloudId}`
        : options.host;

    const scriptUrl = `${host}/${options.trackerFileName}`;
    const trackerUrl = `${host}/matomo.php`;
    let crossorigin: '' | 'anonymous' | 'use-credentials' | undefined = '';

    if (options.crossOrigin && ['anonymous', 'use-credentials'].includes(options.crossOrigin)) {
        crossorigin = options.crossOrigin as '' | 'anonymous' | 'use-credentials' | undefined;
    }

    if (host.includes('matomo.cloud') && options.containerId) {
        host = `https://cdn.matomo.cloud/${new URL(host).hostname}`;

        const url = `${host}/container_${options.containerId}.js`; // Generate container URL

        // Default Matomo Tag Manager snippet
        const _mtm = window._mtm = window._mtm || [];
        _mtm.push({ 'mtm.startTime': (new Date().getTime()), 'event': 'mtm.Start' });

        loadScript(scriptUrl, {
            async: false,
            defer: true,
            crossOrigin: crossorigin
        });

        if (options.debug) {
            console.debug(`[Matomo] Container script "${options.containerId}" has been loaded.`);
        }
    }

    window._paq = window._paq || [];

    window._paq.push(['setTrackerUrl', trackerUrl]);
    window._paq.push(['setSiteId', options.siteId as number]);

    if (options.disableCookies) {
        window._paq.push(['disableCookies']);
    } else {
        window._paq.push(['requireCookieConsent']);
    }

    if (options.enableHeartBeatTimer) {
        window._paq.push([
            'enableHeartBeatTimer',
            options.heartBeatTimerInterval
        ]);
    }

    await loadScript(scriptUrl, {
        async: false,
        defer: true,
        crossOrigin: crossorigin
    });

    const start = Date.now();

    while (!window.Matomo) {
        if (Date.now() - start > options.scriptTimeout) {
            console.warn('[Matomo] Script loaded but Matomo not available.');

            return false;
        }

        await new Promise(r => setTimeout(r, options.scriptInterval));
    }

    const tracker = window.Matomo.getAsyncTracker();

    if (options.enableLinkTracking) {
        tracker.enableLinkTracking();
    }

    tracker.enableJSErrorTracking();

    // Mark as ready
    matomoState.ready = true;

    // Flush queue
    while (matomoState.queue.length) {
        matomoState.queue.shift()?.(tracker);
    }

    // If it is the Consent Studio scanner.
    onConsentStudioScan(() => {
        tracker.setCookieConsentGiven();
    });

    if (options.debug) {
        console.debug('[Matomo] Ready. Queue flushed.');
    }

    return true;
}
